import { useState, useMemo, useEffect, useRef } from 'react';

import { useBrowserStorage } from '@aspendental/shared-utils/react';

import { FACILITY_CODE_STORAGE_KEY } from '@/constants';
import { useAppContext, useFacilityContext } from '@/context';
import { BffBrandType, BffFacility } from '@/graphql/__generated/sdk';
import { getBffFacilitiesByAddress } from '@/services';

export function useLocationManagement() {
	const { environment, appName, appVersion, config } = useAppContext();
	const facilityContext = useFacilityContext(true);
	const { officeInfo, nearbyFacilities, facilityBrand } = facilityContext;

	const [searchResults, setSearchResults] = useState<BffFacility[]>([]);
	const [selectedOfficeCode, setSelectedOfficeCode] = useState<string>(officeInfo?.code || '');
	const [searchError, setSearchError] = useState<string | null>(null);
	const [isSearchResult, setIsSearchResult] = useState(false);
	const [homeUrl, setHomeUrl] = useState('/');

	// Bumped on every request that actually goes out, so a response can tell
	// whether a newer search has started since it left.
	const searchIdRef = useRef(0);

	const useSessionStorageFacilityCode = config.featureFlags?.useSessionStorageFacilityCode;
	const { setValue } = useBrowserStorage({
		key: FACILITY_CODE_STORAGE_KEY,
		storage: useSessionStorageFacilityCode ? globalThis.sessionStorage : globalThis.localStorage,
	});

	const nearbyOfficesWithoutSelected = nearbyFacilities?.filter(
		(facility: BffFacility) => facility.code !== officeInfo?.code
	);

	const url =
		environment === 'prod' ? config.services.prod.BEFFE_GRAPHQL_URL : config.services.nonprod.BEFFE_GRAPHQL_URL;

	const displayedOffices = useMemo(() => {
		// Only prepend selected office when showing initial nearby offices, not search results
		if (isSearchResult) {
			return searchResults || [];
		}
		return officeInfo
			? [
					{
						...officeInfo,
					},
					...(nearbyOfficesWithoutSelected || []),
			  ]
			: nearbyOfficesWithoutSelected;
	}, [searchResults, officeInfo, isSearchResult, nearbyOfficesWithoutSelected]);

	useEffect(() => {
		if (globalThis.window !== undefined) {
			setHomeUrl(globalThis.location.origin + '/');
		}
	}, []);

	const handleSetAsMyLocationClick = (trackFacilitySet: (facility: BffFacility) => void) => {
		if (selectedOfficeCode) {
			const facilitiesToLook = isSearchResult ? searchResults : nearbyFacilities;
			const newSelectedOffice = facilitiesToLook?.find((item: BffFacility) => item.code === selectedOfficeCode);
			if (!newSelectedOffice) {
				setSelectedOfficeCode('');
				return;
			}
			setValue(selectedOfficeCode);
			trackFacilitySet(newSelectedOffice);
		}
	};

	/**
	 * Searches can overlap: the input fires on every Enter press and icon click with no
	 * debounce, disabled state, or spinner, so a slow request can resolve after a newer one.
	 * Only the newest search is allowed to write state — a stale response landing last would
	 * otherwise leave the "no results" banner sitting above a list of offices from a
	 * different query.
	 */
	const handleLocationSearch = async (query: string) => {
		const noResultsMessage = 'Sorry, no results were found in the immediate area. Please try another search.';
		const clearSearchResults = () => {
			setIsSearchResult(false);
			setSelectedOfficeCode('');
		};

		setSearchError(null);

		if (!query.trim()) return;

		const searchId = ++searchIdRef.current;
		const isStale = () => searchId !== searchIdRef.current;

		const showNoResults = () => {
			if (isStale()) return;
			clearSearchResults();
			setSearchError(noResultsMessage);
		};

		try {
			const facilitiesListByAddress = await getBffFacilitiesByAddress(
				facilityBrand.toString() as BffBrandType,
				query.trim(),
				url,
				true,
				appName,
				appVersion
			);

			const facilities = facilitiesListByAddress?.facilitiesByAddress || [];

			if (facilities.length === 0) {
				showNoResults();
				return;
			}

			if (!isStale()) {
				setSearchResults(facilities as BffFacility[]);
				setIsSearchResult(true);
			}
		} catch (error) {
			showNoResults();
			console.error('Location search error:', error);
		}
	};

	const resetLocationState = () => {
		setIsSearchResult(false);
		setSearchError(null);
		setSearchResults([]);
	};

	return {
		selectedOfficeCode,
		setSelectedOfficeCode,
		searchError,
		isSearchResult,
		homeUrl,
		displayedOffices,
		handleSetAsMyLocationClick,
		handleLocationSearch,
		resetLocationState,
	};
}
